import React from "react";
import PropTypes from "prop-types";
import { Navigate } from "react-router-dom";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/features/core/auth/context/AuthContext";
import { Spinner } from "@homy/ui";

/**
 * PermissionGate
 * Declarative component that conditionally renders its children if the active
 * user satisfies the specified Profile, AuthLevel, capability, or predicate.
 */
export function PermissionGate({
  profile,
  level,
  role,
  authLevel,
  minLevel,
  permission,
  anyPermissions,
  allPermissions,
  predicate,
  inverse = false,
  fallback = null,
  children,
}) {
  const perms = usePermissions();

  if (perms.loading) {
    return fallback;
  }

  if (!perms.isAuthenticated) {
    return inverse ? <>{children}</> : fallback;
  }

  let predicatePassed = true;
  if (predicate) {
    try {
      predicatePassed = Boolean(predicate(perms));
    } catch {
      predicatePassed = false;
    }
  }

  const effectiveMinLevel = minLevel || authLevel;
  const effectiveExactLevel = level || role;

  const isAllowed =
    perms.canAccess({
      profile,
      level: effectiveExactLevel,
      minLevel: effectiveMinLevel,
      permission,
      anyPermissions,
      allPermissions,
    }) && predicatePassed;

  const shouldRender = inverse ? !isAllowed : isAllowed;

  if (!shouldRender) {
    return fallback;
  }

  return <>{children}</>;
}

PermissionGate.propTypes = {
  inverse: PropTypes.bool,
  profile: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  level: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  role: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  authLevel: PropTypes.string,
  minLevel: PropTypes.string,
  permission: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  anyPermissions: PropTypes.arrayOf(PropTypes.string),
  allPermissions: PropTypes.arrayOf(PropTypes.string),
  predicate: PropTypes.func,
  fallback: PropTypes.node,
  children: PropTypes.node.isRequired,
};

/**
 * RequirePermission
 * Route wrapper component for React Router routes.
 */
export function RequirePermission({
  profile,
  level,
  role,
  authLevel,
  minLevel,
  permission,
  anyPermissions,
  allPermissions,
  predicate,
  redirectTo = "/",
  unauthenticatedRedirect = "/login",
  fallback = null,
  children,
}) {
  const { isAuthenticated, loading } = useAuth();
  const perms = usePermissions();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-transparent">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={unauthenticatedRedirect} replace />;
  }

  let predicatePassed = true;
  if (predicate) {
    try {
      predicatePassed = Boolean(predicate(perms));
    } catch {
      predicatePassed = false;
    }
  }

  const effectiveMinLevel = minLevel || authLevel;
  const effectiveExactLevel = level || role;

  const isAllowed =
    perms.canAccess({
      profile,
      level: effectiveExactLevel,
      minLevel: effectiveMinLevel,
      permission,
      anyPermissions,
      allPermissions,
    }) && predicatePassed;

  if (!isAllowed) {
    return fallback || <Navigate to={redirectTo} replace />;
  }

  return children;
}

RequirePermission.propTypes = {
  profile: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  level: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  role: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  authLevel: PropTypes.string,
  minLevel: PropTypes.string,
  permission: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  anyPermissions: PropTypes.arrayOf(PropTypes.string),
  allPermissions: PropTypes.arrayOf(PropTypes.string),
  predicate: PropTypes.func,
  redirectTo: PropTypes.string,
  unauthenticatedRedirect: PropTypes.string,
  fallback: PropTypes.node,
  children: PropTypes.node.isRequired,
};

export default PermissionGate;
